import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, ArrowRight, Users } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { Seo } from "@/components/Seo";
import { divisionTheme } from "@/lib/divisionTheme";

function splitPlayers(str) {
  if (!str) return [];
  return str
    .split(/,|&|\band\b|\n/gi)
    .map((s) => s.trim())
    .filter(Boolean);
}

export default function Champions() {
  const [champions, setChampions] = useState(null);

  useEffect(() => {
    api
      .get("/divisions")
      .then((r) => setChampions((r.data || []).filter((d) => d.archived && d.champion_team)))
      .catch(() => setChampions([]));
  }, []);

  return (
    <Layout>
      <Seo title="Quinte BCA Champions & Past Pool League Results" description="Celebrate Quinte BCA Pool League champions and past division results from Trenton and the Quinte region of Ontario." path="/champions" />
      <PageHeader
        eyebrow="Hall of fame"
        title="Past Champions"
        subtitle="Celebrating the teams who took first place in our completed sessions."
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        {!champions ? (
          <p className="text-[#555555]">Loading champions…</p>
        ) : champions.length === 0 ? (
          <p className="text-[#555555]">Champions will appear here as sessions are completed.</p>
        ) : (
          <div className="grid gap-8 md:grid-cols-2" data-testid="champions-grid">
            {champions.map((d) => {
              const theme = divisionTheme(d.color);
              return (
              <div
                key={d.id}
                data-testid={`champion-card-${d.slug}`}
                style={{ ...theme.vars, borderTopColor: "var(--div-accent)", borderTopWidth: "3px" }}
                className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm overflow-hidden hover:shadow-lg transition-all"
              >
                {d.champion_image && (
                  <Link to={`/divisions/${d.slug}`} className="block bg-[#0B0B0C]">
                    <img src={fileUrl(d.champion_image)} alt={`${d.champion_team} champions`} className="w-full h-auto object-contain" />
                  </Link>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-2 text-[var(--div-accent)] mb-1">
                    <Trophy className="h-5 w-5" />
                    <span className="text-xs font-semibold uppercase tracking-wide">
                      {d.name}{d.season ? ` · ${d.season}` : ""}
                    </span>
                  </div>
                  <h3 className="font-heading font-extrabold text-2xl whitespace-pre-line leading-tight">{d.champion_team}</h3>
                  {splitPlayers(d.champion_players).length > 0 && (
                    <div className="mt-3 flex items-start gap-2 text-[#333333]">
                      <Users className="h-4 w-4 mt-1 text-[var(--div-accent)] shrink-0" />
                      <ul className="flex flex-wrap gap-x-2 gap-y-1 text-sm">
                        {splitPlayers(d.champion_players).map((p, i) => (
                          <li key={i} className="after:content-['•'] after:ml-2 after:text-gray-300 last:after:content-['']">
                            {p}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <Link
                    to={`/divisions/${d.slug}`}
                    data-testid={`champion-results-${d.slug}`}
                    className="inline-flex items-center gap-1 text-[var(--div-accent)] font-semibold text-sm mt-5 hover:gap-2 transition-all"
                  >
                    View final standings <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
